import * as React from 'react'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import Card from '@mui/material/Card'
import Avatar from '@mui/material/Avatar'
import CardContent from '@mui/material/CardContent'
import { SendOutline } from 'mdi-material-ui'
import { useMutation } from 'react-query'
import {
  createComment,
  createCommentImport,
  CreateVariantCommentDto,
  getAllComments,
  removeComment,
  updateComment,
  UpdateVariantCommentDto,
  VariantChrom
} from '@/@core/services/app.service'
import { formatDate } from '@/@core/utils/utils'
import Icon from '@/@core/components/icon'
import { useTranslation } from 'react-i18next'

interface Comment {
  post_time: string
  userName: string
  postTime: string
  content: string
  _id: string
  user_data_name: string
  key: string
}

const reject = [
  {
    title:
      'Mode of disease inheritance is autosomal recessive and only one heterozygous variant is found in the sample.'
  },
  {
    title: 'ACMG classifies to be BENIGN variant.'
  },
  {
    title:
      'Insilico Missense prediction tools predict this variant it to be BENIGN/NON-DAMAGING to protein function.'
  },
  {
    title:
      'Insilico splice predicition tools ( ASSP and/or NNsplice)  predicts this variant does not have any impact on splicing (NON-DAMAGING). '
  },
  {
    title: 'High MAF and reported in homozygous individuals in ExAC database. '
  },
  {
    title:
      'The gene-disease association are not relevant to sample phenotypes. No genotype-Phenotype correlation. '
  },
  {
    title:
      'Insilico,ACMG,HGMD and ClinVar classifies it to be a LIKELY BENIGN/BENIGN variant. '
  },
  {
    title: 'ClinVar classifies it to be a LIKELY BENIGN/BENIGN variant. '
  },
  {
    title: 'Silent variant not found at the intron-exon junction. '
  },
  {
    title:
      'Missense variant found at the intron-exon junction and predicted to be NON-DAMAGING by insilico splice tools ( ASSP and/or NNsplice). '
  },
  {
    title: 'Low coverage zygosity.'
  },
  {
    title:
      'Sequencing Artifact- Two or more variants found in the near by vicnity.'
  },
  {
    title:
      'This gene has putative psuedogenes, needs alternative tools/methods for confirmation regarding this variant for its presence in pseudogene or not.'
  },
  {
    title:
      'Intronic variant found within + or - 25bp region and insilico prediction tools (ASSP and/or NNsplice) predicts it to be NON-DAMAGING wrt splicing.'
  },
  {
    title:
      'Conflicting interpretations in Clinvar wrt to this variant as VUS/Likely benign.'
  },
  {
    title:
      'Functional study data on this variant reported  with a BENIGN  impact on the gene/protein function. '
  },
  {
    title:
      'The variant is present in low complexity region/high-repeat homopolymers region.'
  },
  {
    title:
      'The variant is present in 3 UTR5 UTR region, the impact of the variant on the gene is unknown unless there is literature evidence.'
  },
  {
    title: 'CADD SCORE is less than 14. '
  },
  {
    title:
      'High total count in heterozygous/homozygous individuals in the gnomAD database. '
  },
  {
    title:
      'This region of IGV looks bad and too many variants in the nearby vicinity. '
  }
]
const acceptances = [
  {
    title:
      'Gene-disease association is well established and a disease-causing variant has been identified in this sample.'
  },
  {
    title:
      'Splicing variant predicted to affect splicing by NNsplice and ASSP. '
  },
  {
    title:
      'Clinvar, HGMD and ACMG classifies it to be Pathogenic/Likely Pathogenic variant.'
  },
  {
    title:
      'Previously reported in publications in patients with sample or sample related phenotype/syndromes and classified as disease-causing. '
  },
  {
    title:
      'Functional study data with an reported impact on the gene/protein function. '
  },
  {
    title: 'The disease inheritance matching to sample phenotypes .'
  },
  {
    title:
      'Autosomal recessive inheritance and two compound heterozygous variants are idnetified in this sample relevant to sample phenotypes. '
  },
  {
    title:
      'Low MAF, and not reported in homozygous individuals in ExAC database  .'
  },
  {
    title: 'P/LP/LOF variant from ACMG recommended 59 genes. '
  },
  {
    title:
      'Silent variant predicted to affect splicing (DAMAGING) by NNsplice and ASSP. '
  },
  {
    title:
      'Missense variant found at the intron-exon junction and predicted to be DAMAGING by insilico splice tools ( ASSP and/or NNsplice). '
  }
]
const Comments = React.forwardRef(
  (props: { row: any; analysisInfo: any }, ref) => {
    const { t } = useTranslation()
    const [rejection, setRejection] = React.useState('')
    const [acceptance, setAcceptance] = React.useState('')
    const [textValue, setTextValue] = React.useState('')
    const [list, setList] = React.useState<string[]>([])
    const [type, setType] = React.useState<string>('')
    const [key, setKey] = React.useState<string>('')
    const [comments, setComments] = React.useState<Comment[]>([])

    const handleRejectionChange = (event: SelectChangeEvent) => {
      // if (!list.some((item) => item === (event.target.value as string)))
      setRejection(event.target.value as string)

      setAcceptance('')
    }

    const handleAcceptanceChange = (event: SelectChangeEvent) => {
      // if (!list.some((item) => item === (event.target.value as string)))
      setAcceptance(event.target.value as string)

      setRejection('')
    }

    const reset = () => {
      setRejection('')

      setAcceptance('')

      setTextValue('')

      setList([])
    }

    const getAll = () => {
      const user = JSON.parse(localStorage.getItem('user') as string)

      const params: CreateVariantCommentDto = {
        chrom: String(props.row?.chrom) as unknown as VariantChrom,
        pos: props.row?.pos as number,
        REF: props.row?.REF as string,
        ALT: props.row?.ALT as string,
        postTime: formatDate(new Date()) as string,
        genomeBuild: props.analysisInfo.genomeBuild,
        content: '12',
        userName: user.email
      }

      GetAllComments.mutateAsync(params)
    }

    const post = () => {
      const user = JSON.parse(localStorage.getItem('user') as string)

      const params: CreateVariantCommentDto = {
        chrom: String(props.row?.chrom) as unknown as VariantChrom,
        pos: props.row?.pos as number,
        REF: props.row?.REF as string,
        ALT: props.row?.ALT as string,
        postTime: formatDate(new Date()) as string,
        genomeBuild: props.analysisInfo.genomeBuild,
        content: textValue,
        userName: user.email
      }

      CreateComment.mutateAsync(params)

      reset()
    }

    const GetAllComments = useMutation({
      mutationFn: async (data: CreateVariantCommentDto) =>
        await getAllComments(data),
      onSuccess: (data: any) => {
        //post返回的结果
        const list: Comment[] = JSON.parse(data.variantComment).comments

        //

        setKey(JSON.parse(data.variantComment).key)

        setComments([...list])
      }
    })

    const CreateComment = useMutation({
      mutationFn: async (data: CreateVariantCommentDto) =>
        await createComment(data),
      onSuccess: (data: any) => {
        //post返回的结果
        getAll()

        setTextValue('')
      }
    })

    const CreateCommentImport = useMutation({
      mutationFn: async (data: CreateVariantCommentDto) =>
        await createCommentImport(data),
      onSuccess: (data: any) => {
        //post返回的结果
        getAll()
      }
    })

    const UpdateComment = useMutation({
      mutationFn: async (data: {
        id: string
        params: UpdateVariantCommentDto
      }) => await updateComment(data),
      onSuccess: (data: any) => {
        //post返回的结果
        getAll()

        setTextValue('')
      }
    })

    const RemoveComment = useMutation({
      mutationFn: async (data: string) => await removeComment(data),
      onSuccess: (data: any) => {
        //post返回的结果
        getAll()
      }
    })

    React.useEffect(() => {
      if (props.row && props.row.user_variant_comments) {
        setComments(props.row.user_variant_comments.comments)
      }
    }, [props.row])

    React.useImperativeHandle(ref, () => {
      return {
        comments,
        key
      }
    })

    const render = () => {
      return (
        <Box paddingBottom={2}>
          {comments.map((items) => {
            return (
              <Card
                sx={{
                  border: 0,
                  boxShadow: 0,
                  color: 'common.black',
                  marginTop: 2
                }}
              >
                <CardContent
                  sx={{
                    p: (theme) => `${theme.spacing(3.25, 5, 4.5)} !important`
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      display: 'flex',
                      mb: 2.75,
                      alignItems: 'center',
                      color: 'common.black',
                      '& svg': { mr: 2.5 }
                    }}
                  >
                    <Avatar
                      alt="Mary Vaughn"
                      src="/images/avatars/4.png"
                      sx={{ width: 34, height: 34, mr: 2.75 }}
                    />

                    {items.user_data_name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: 'common.black' }}
                  >
                    {t(items.content)}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'common.black' }}
                      >
                        {formatDate(items.post_time)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mr: 3.5,
                          '& svg': { mr: 1.25 }
                        }}
                      >
                        <Button
                          onClick={() => {
                            setType(items._id)

                            setTextValue(items.content)

                            setList(
                              items.content.split('\n').filter((item) => item)
                            )
                          }}
                          size="small"
                          variant="contained"
                        >
                          {t('edit')}
                        </Button>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          '& svg': { mr: 1.25 }
                        }}
                      >
                        <Button
                          onClick={() => {
                            RemoveComment.mutateAsync(items._id)
                          }}
                          size="small"
                          variant="contained"
                        >
                          {t('delete')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )
          })}
        </Box>
      )
    }

    React.useEffect(() => {
      if (rejection && !list.some((i) => i === rejection))
        setList([...list, rejection])
      if (acceptance && !list.some((i) => i === acceptance))
        setList([...list, acceptance])
    }, [rejection, acceptance])

    React.useEffect(() => {
      setTextValue(list.reduce((acc, cur) => cur + '\n' + acc, ''))
    }, [list])

    return (
      <Box padding={'0 20px'} maxWidth={800}>
        <Box
          height={'50vh'}
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
            bgcolor: 'rgb(238, 238, 238)',
            padding: '0 8px',
            marginBottom: 5
          }}
        >
          {comments.length ? (
            render()
          ) : (
            <Typography marginTop={10} textAlign={'center'}>
              {t('No comments to show.')}
            </Typography>
          )}
        </Box>

        <Stack direction={'row'} spacing={6}>
          <Box flex={1} maxWidth={'50%'}>
            <FormControl fullWidth size="small">
              <InputLabel id="demo-simple-select-label">
                {t('REJECTION COMMENTS')}
              </InputLabel>

              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={rejection}
                label={t('REJECTION COMMENTS')}
                onChange={handleRejectionChange}
              >
                {reject.map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      value={`[${t('REJECTION')}] ${t(item.title)}`}
                    >
                      {t(item.title)}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>

          <Box flex={1} maxWidth={'50%'}>
            <FormControl fullWidth size="small">
              <InputLabel id="demo-simple-select-label">
                {t('ACCEPTANCE COMMENTS')}
              </InputLabel>

              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={acceptance}
                label={t('ACCEPTANCE COMMENTS')}
                onChange={handleAcceptanceChange}
              >
                {acceptances.map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      value={`[${t('ACCEPTANCE')}] ${t(item.title)}`}
                    >
                      {t(item.title)}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>

          <Button onClick={reset} variant="contained">
            {t('reset')}
          </Button>
        </Stack>

        <Stack marginTop={5} direction={'row'} spacing={6} alignItems="center">
          <Box flex={1}>
            <TextField
              id="outlined-multiline-static"
              multiline
              rows={4}
              placeholder={t('add your comment...')}
              fullWidth
              autoFocus
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
            />
          </Box>

          <Box>
            <Stack spacing={2}>
              {type ? (
                <React.Fragment>
                  <Button
                    onClick={() => {
                      setType('')
                      const user = JSON.parse(
                        localStorage.getItem('user') as string
                      )

                      const params: CreateVariantCommentDto = {
                        chrom: String(
                          props.row?.chrom
                        ) as unknown as VariantChrom,
                        pos: props.row?.pos as number,
                        REF: props.row?.REF as string,
                        ALT: props.row?.ALT as string,
                        postTime: formatDate(new Date()) as string,
                        genomeBuild: props.analysisInfo.genomeBuild,
                        content: textValue,
                        userName: user.email
                      }

                      UpdateComment.mutateAsync({
                        id: type,
                        params
                      })
                    }}
                    variant="contained"
                    startIcon={<SendOutline />}
                  >
                    {t('edit')}
                  </Button>

                  <Button
                    onClick={() => {
                      setType('')

                      setTextValue('')
                    }}
                    variant="contained"
                    startIcon={<SendOutline />}
                  >
                    {t('cancel')}
                  </Button>
                </React.Fragment>
              ) : (
                <Button
                  onClick={post}
                  variant="contained"
                  startIcon={<SendOutline />}
                >
                  {t('Post')}
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    )
  }
)

export default Comments
